import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useQuery } from '@tanstack/react-query';
import chatbotService from '../api/chatbotService';

const Billing = () => {
  const { projectId } = useParams();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Fetch billing report
  const { data: report, isLoading } = useQuery({
    queryKey: ['billingReport', projectId, selectedYear, selectedMonth],
    queryFn: () => chatbotService.generateBillingReport(projectId, selectedYear, selectedMonth),
  });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">Billing & Cost</h1>
          <div className="flex gap-2">
            <select
              className="form-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {months.map((month, index) => (
                <option key={month} value={index + 1}>
                  {month}
                </option>
              ))}
            </select>
            <select
              className="form-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="card mt-4 p-4">
            <p>Loading billing report...</p>
          </div>
        ) : report ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="card p-4">
              <h2 className="text-lg font-medium mb-4">Cost Breakdown</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Primary LLM</span>
                  <span className="font-medium">
                    {formatCurrency(report?.primaryLLM)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Secondary LLM</span>
                  <span className="font-medium">
                    {formatCurrency(report?.secondaryLLM)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Knowledge Base</span>
                  <span className="font-medium">
                    {formatCurrency(report?.knowledgeBaseCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">API Calls</span>
                  <span className="font-medium">
                    {formatCurrency(report?.apiCost)}
                  </span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total Cost</span>
                    <span className="font-medium text-lg">
                      {formatCurrency(report?.totalCost)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <h2 className="text-lg font-medium mb-4">Usage Statistics</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Sessions</span>
                  <span className="font-medium">{report.sessionCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Messages</span>
                  <span className="font-medium">{report.totalMessages}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total API Calls</span>
                  <span className="font-medium">{report.totalApiCalls}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Tokens Used</span>
                  <span className="font-medium">{report.totalTokens}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Messages per Session</span>
                  <span className="font-medium">{report.messagePerSession}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Cost per Session</span>
                  <span className="font-medium">
                    {formatCurrency(report.costPerSession)}
                  </span>
                </div>
              </div>
            </div>

            <div className="card p-4 md:col-span-2">
              <h2 className="text-lg font-medium mb-4">Billing Information</h2>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Report generated on: {new Date(report.generatedAt).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">
                  Billing period: {months[selectedMonth - 1]} {selectedYear}
                </p>
                <p className="text-sm text-gray-600">
                  Note: All costs are estimates and may be subject to change.
                  Final charges will be reflected in your monthly invoice.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card mt-4 p-4">
            <p>No billing data available for the selected period.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Billing; 